import { Trash2 } from 'lucide-react'
import { type ChangeEvent, useState } from 'react'

import { useCreateQualifyingLifeEvent } from 'components/hooks/qualifying_life_event/useCreateQualifyingLifeEvent'
import AnimateHeight from 'components/reusable/AnimateHeight'
import AnimateOpacity from 'components/reusable/AnimateOpacity'
import OpacityTransition from 'components/reusable/OpacityTransition'
import { membersQualifyingLifeEventsParticipants } from 'rails_routes'
import {
  Banner,
  Button,
  FileList,
  FileUpload,
  IconButton,
  List,
  Select,
} from 'slab'
import { useWindowLocation } from 'utils/windowLocation'

const NewbornDocumentation = () => (
  <>
    <List>
      <List.Item>
        <b>Birth certificate</b> (once available), or
      </List.Item>
      <List.Item>
        <b>Birth notice</b> (a hospital birth confirmation with baby's name,
        date of birth, and parent(s)' names)
      </List.Item>
    </List>
  </>
)
const MarriageDocumentation = () => (
  <>
    <List>
      <List.Item>
        <b>Marriage certificate</b>, or
      </List.Item>
      <List.Item>
        <b>Marriage license</b>
      </List.Item>
    </List>
  </>
)

const AdoptionDocumentation = () => (
  <>
    <List>
      <List.Item>
        <b>Adoption papers</b>,
      </List.Item>
      <List.Item>
        <b>Placement documentation</b> from the state or adoption agency, or
      </List.Item>
      <List.Item>
        <b>Court order</b> granting custody or guardianship
      </List.Item>
    </List>
  </>
)

const LossOfCoverageDocumentation = () => (
  <>
    <List>
      <List.Item>
        <b>Loss of coverage letter</b> from employer or prior insurance, that
        includes:
        <List>
          <List.Item>
            <b>Name(s) of who lost coverage</b>,
          </List.Item>
          <List.Item>
            <b>Last day of coverage</b>, and
          </List.Item>
          <List.Item>
            <b>Reason for loss</b> (e.g., job loss, aging out of parent's plan,
            divorce)
          </List.Item>
        </List>
      </List.Item>
    </List>
  </>
)

type StartDateOverviewProps = {
  effectiveDate: string
}

const NewbornStartDateOverview = (_props: StartDateOverviewProps) => (
  <Banner
    type='info'
    title='Your start date will be retroactive to the date of birth'
  >
    Your plan should backdate your baby's coverage to the day they were born, as
    long as you submit their information within 60 days. (Congrats, btw!)
  </Banner>
)

const MarriageStartDateOverview = ({
  effectiveDate,
}: StartDateOverviewProps) => (
  <Banner
    type='info'
    title={`Your estimated start date will be ${effectiveDate}`}
  >
    You have 60 days from your marriage date to make any plan changes. They'll
    go into effect on {effectiveDate} if submitted today. (Congrats,
    btw!)
  </Banner>
)

const AdoptionStartDateOverview = (_props: StartDateOverviewProps) => (
  <Banner
    type='info'
    title='Your start date will be retroactive to date of guardianship'
  >
    Your plan should backdate your child's coverage to the day they were
    adopted, as long as you submit their information within 60 days.
  </Banner>
)

const LossOfCoverageStartDateOverview = ({
  effectiveDate,
}: StartDateOverviewProps) => (
  <Banner type='info' title={`Your estimated start date will be ${effectiveDate}`}>
    You have 60 days from the date of coverage loss to make any changes to your
    own plan. They'll go into effect on {effectiveDate} if submitted
    today.
  </Banner>
)

const requiredDocumentation = {
  newborn: <NewbornDocumentation />,
  marriage: <MarriageDocumentation />,
  adoption: <AdoptionDocumentation />,
  loss_of_coverage: <LossOfCoverageDocumentation />,
}

const startDateOverview = {
  newborn: NewbornStartDateOverview,
  marriage: MarriageStartDateOverview,
  adoption: AdoptionStartDateOverview,
  loss_of_coverage: LossOfCoverageStartDateOverview,
}

function getStartDateOverviewComponent (event: string, effectiveDate: string) {
  const effectiveDateDate = new Date(effectiveDate)
  const effectiveDateString = effectiveDateDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })

  const StartDateOverviewComponent =
    startDateOverview[event as keyof typeof startDateOverview]
  return <StartDateOverviewComponent effectiveDate={effectiveDateString} />
}

const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf']
const maxSize = 10 * 1024 * 1024 // 10MB in bytes

export interface QualifyingLifeEventViewProps {
  effectiveDate: string
}

export const QualifyingLifeEventViewImpl = ({
  effectiveDate,
}: QualifyingLifeEventViewProps) => {
  const { replace } = useWindowLocation()

  const [selectedEvent, setSelectedEvent] = useState<string | undefined>()

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [uploadError, setUploadError] = useState<string | null>(null)

  const createQualifyingLifeEventMutation = useCreateQualifyingLifeEvent()

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length === 0) return

    // Validate each file
    const validFiles = files.filter((file) => {
      if (!allowedTypes.includes(file.type)) {
        setUploadError(`File ${file.name} must be a JPG, PNG, or PDF`)
        return false
      }
      if (file.size > maxSize) {
        setUploadError(`File ${file.name} must be less than 10MB`)
        return false
      }
      return true
    })

    if (validFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...validFiles])
      setUploadError(null)
    }
  }

  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = () => {
    if (!selectedEvent) return

    createQualifyingLifeEventMutation.mutate(
      {
        situation: selectedEvent,
        files: uploadedFiles,
      },
      {
        onSuccess: (data) => {
          replace(membersQualifyingLifeEventsParticipants.index({ qualifying_life_event_id: data.id }))
        },
      }
    )
  }

  return (
    <OpacityTransition>
      <div className='flex flex-col gap-4 max-w-xl mx-auto px-6 md:px-12'>
        <div className='space-y-2'>
          <div className='text-2xl font-bold'>Change who's covered</div>
          <div data-testid='qualifying-life-event-description'>
            You can update who's on your plan (and what allowance you get as a
            result) if you've recently experienced a{' '}
            <a href='https://support.thatch.com/qualify-life-event'>
              qualifying life event
            </a>
            .
          </div>
        </div>
        <div className='font-semibold text-lg'>What's changed?</div>
        <Select
          label='Qualifying life event'
          value={selectedEvent}
          onChange={(e) => setSelectedEvent(e.target.value)}
          placeholder='Select a qualifying life event'
          data-testid='qualifying-life-event-select'
          description="Don't see your qualifying life event? Reach out to support@thatch.com."
        >
          <option value='newborn'>Had a new baby</option>
          <option value='marriage'>Got married</option>
          <option value='adoption'>Adopted a child</option>
          <option value='loss_of_coverage'>Dependent lost coverage</option>
        </Select>
        {selectedEvent && (
          <AnimateHeight>
            <AnimateOpacity isOpen={!!selectedEvent} key={selectedEvent}>
              <div className='space-y-4'>
                {getStartDateOverviewComponent(selectedEvent, effectiveDate)}

                <div className='font-semibold text-lg pt-6'>
                  Upload documents
                </div>

                <div>
                  To make changes to your insurance, we need to verify when the
                  qualifying life event occurred. Below are the documents that
                  will be accepted by your insurance company:
                </div>

                {
                  requiredDocumentation[
                    selectedEvent as keyof typeof requiredDocumentation
                  ]
                }

                <FileUpload
                  onChange={(e) => handleFileUpload(e)}
                  error={uploadError}
                  disabled={createQualifyingLifeEventMutation.isPending}
                  multiple // Enable multiple file selection.
                  accept='.pdf,.jpg,.jpeg,.png'
                  data-testid='file-upload'
                >
                  <FileUpload.Content />
                </FileUpload>

                <AnimateOpacity isOpen={!!uploadedFiles.length}>
                  <FileList>
                    {uploadedFiles.map((file, index) => (
                      <FileList.File
                        key={index}
                        name={file.name}
                        size={file.size}
                        status='success'
                        actions={
                          <IconButton
                            disabled={
                              createQualifyingLifeEventMutation.isPending
                            }
                            onClick={() => removeFile(index)}
                            label='Remove file'
                            icon={Trash2}
                          />
                        }
                      />
                    ))}
                  </FileList>
                </AnimateOpacity>
              </div>
            </AnimateOpacity>
          </AnimateHeight>
        )}
        <div className='pt-6 flex justify-between'>
          <Button
            onClick={() => replace('/')}
            data-testid='button-dashboard'
          >
            Back to dashboard
          </Button>
          <Button
            variant='primary'
            disabled={!uploadedFiles.length}
            onClick={handleSubmit}
            data-testid='button-continue'
          >
            Next
          </Button>
        </div>
      </div>
    </OpacityTransition>
  )
}

const QualifyingLifeEventView = (props: QualifyingLifeEventViewProps) => {
  return <QualifyingLifeEventViewImpl {...props} />
}

export default QualifyingLifeEventView
